import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  defaultQuotationsListSort,
  adminWorkorderQuotationsListFilters,
  emptyQuotationsListFilters,
  type QuotationsListFilters,
  type QuotationsListSort,
  type QuotationPdfKind,
  type StrapiQuotationRow,
} from '../../../../api/strapiCrm';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from '../../../../components/DataGridNumberedPagination';
import { QuotationDataGrid } from '../../../../components/QuotationDataGrid';
import { QuotationPreviewDialog } from '../../QuotationPreviewDialog';
import { QuotationPdfPreviewDialog } from '../../QuotationPdfPreviewDialog';
import { useAuth } from '../../../../auth/AuthContext';
import { resolveSamplingSchedulePath } from '../../../SamplingSchedulesPage/resolveSamplingSchedulePath';
import type { QuotationsListVisibility } from '../../../../api/strapiCrm';
import {
  useCopyQuotationMutation,
  useQuotationsListQuery,
  useNavigationMenuQuery,
  useUsersQuery,
} from '../../../../query/hooks';
import {
  canCreateQuotation,
  isAdminWorkOrderPic,
  isMarketingPic,
  isMarketingSpv,
  isSuperadminUser,
  resolveQuotationWorkflowUser,
} from '../../quotationWorkflow';
import { LIST_FILTERS_APPLY_HINT, commitListFiltersOnEnter } from '../../../../ui/listFiltersApi';
import { CollapsibleListFilters } from '../../../../ui/CollapsibleListFilters';
import { QUOTATION_STATUS_OPTIONS } from '../../QuotationFormRoute/quotationFormConstants';

const DEFAULT_QUOTATION_SORT_MODEL: GridSortModel = [
  { field: defaultQuotationsListSort.field, sort: defaultQuotationsListSort.direction },
];

function gridSortModelToListSort(model: GridSortModel): QuotationsListSort {
  const first = model[0];
  if (first?.field && first.sort) {
    return { field: first.field, direction: first.sort };
  }
  return { ...defaultQuotationsListSort };
}

const QUOTATION_FILTER_STATUS_ALL = 'ALL' as const;

type QuotationFilterStatus =
  | typeof QUOTATION_FILTER_STATUS_ALL
  | (typeof QUOTATION_STATUS_OPTIONS)[number]['value'];

const quotationFilterStatusLabel: Record<QuotationFilterStatus, string> = {
  [QUOTATION_FILTER_STATUS_ALL]: '(Semua status)',
  ...(Object.fromEntries(QUOTATION_STATUS_OPTIONS.map((o) => [o.value, o.label])) as Record<
    (typeof QUOTATION_STATUS_OPTIONS)[number]['value'],
    string
  >),
};

const QUOTATION_CONTRACT_ALL = 'ALL' as const;
const QUOTATION_CONTRACT_OPTIONS = [QUOTATION_CONTRACT_ALL, 'contract', 'non_contract'] as const;
type QuotationContractUi = (typeof QUOTATION_CONTRACT_OPTIONS)[number];

const quotationContractLabel: Record<QuotationContractUi, string> = {
  [QUOTATION_CONTRACT_ALL]: '(Semua)',
  contract: 'Kontrak',
  non_contract: 'Non kontrak',
};

const QUOTATION_CONSULTANT_ALL = 'ALL' as const;
const QUOTATION_CONSULTANT_OPTIONS = [QUOTATION_CONSULTANT_ALL, 'yes', 'no'] as const;
type QuotationConsultantUi = (typeof QUOTATION_CONSULTANT_OPTIONS)[number];

const quotationConsultantLabel: Record<QuotationConsultantUi, string> = {
  [QUOTATION_CONSULTANT_ALL]: '(Semua)',
  yes: 'Ya',
  no: 'Tidak',
};

type QuotationFilterDraft = {
  quotationNumber: string;
  customer: string;
  assigneeContains: string;
  approverContains: string;
  status: QuotationFilterStatus;
  contract: QuotationContractUi;
  consultantQuote: QuotationConsultantUi;
};

const initialQuotationDraft: QuotationFilterDraft = {
  quotationNumber: '',
  customer: '',
  assigneeContains: '',
  approverContains: '',
  status: QUOTATION_FILTER_STATUS_ALL,
  contract: QUOTATION_CONTRACT_ALL,
  consultantQuote: QUOTATION_CONSULTANT_ALL,
};

function adminWorkorderQuotationDraft(): QuotationFilterDraft {
  return {
    ...initialQuotationDraft,
    status: 'customer_approved',
  };
}

function cloneEmptyQuotationsApplied(): QuotationsListFilters {
  return { ...emptyQuotationsListFilters };
}

function isAdminWorkorderDefaultApplied(f: QuotationsListFilters): boolean {
  return (
    f.status === 'customer_approved' &&
    f.quotationNumber.trim() === '' &&
    f.customer.trim() === '' &&
    f.assigneeContains.trim() === '' &&
    f.approverContains.trim() === '' &&
    f.contract === '' &&
    f.consultantQuote === ''
  );
}

function quotationAppliedIsActive(
  f: QuotationsListFilters,
  isAdminWo = false,
): boolean {
  if (isAdminWo && isAdminWorkorderDefaultApplied(f)) return false;
  return (
    f.quotationNumber.trim() !== '' ||
    f.customer.trim() !== '' ||
    f.assigneeContains.trim() !== '' ||
    f.approverContains.trim() !== '' ||
    f.status.trim() !== '' ||
    f.contract !== '' ||
    f.consultantQuote !== ''
  );
}

function draftToQuotationApplied(d: QuotationFilterDraft): QuotationsListFilters {
  return {
    quotationNumber: d.quotationNumber.trim(),
    customer: d.customer.trim(),
    assigneeContains: d.assigneeContains.trim(),
    approverContains: d.approverContains.trim(),
    status: d.status === QUOTATION_FILTER_STATUS_ALL ? '' : d.status,
    contract:
      d.contract === QUOTATION_CONTRACT_ALL
        ? ''
        : d.contract === 'contract'
          ? 'contract'
          : 'non_contract',
    consultantQuote:
      d.consultantQuote === QUOTATION_CONSULTANT_ALL
        ? ''
        : d.consultantQuote === 'yes'
          ? 'yes'
          : 'no',
  };
}

function quotationDraftLooksInitial(d: QuotationFilterDraft, isAdminWo = false): boolean {
  if (isAdminWo) {
    return (
      d.quotationNumber.trim() === '' &&
      d.customer.trim() === '' &&
      d.assigneeContains.trim() === '' &&
      d.approverContains.trim() === '' &&
      d.status === 'customer_approved' &&
      d.contract === QUOTATION_CONTRACT_ALL &&
      d.consultantQuote === QUOTATION_CONSULTANT_ALL
    );
  }
  return (
    d.quotationNumber.trim() === '' &&
    d.customer.trim() === '' &&
    d.assigneeContains.trim() === '' &&
    d.approverContains.trim() === '' &&
    d.status === QUOTATION_FILTER_STATUS_ALL &&
    d.contract === QUOTATION_CONTRACT_ALL &&
    d.consultantQuote === QUOTATION_CONSULTANT_ALL
  );
}

/** Data grid: quotation (marketing) dari Strapi. */
export function QuotationListTabScreen() {
  const { jwt, user } = useAuth();
  const navigate = useNavigate();
  const copyMut = useCopyQuotationMutation(jwt);

  const isAdminWo = isAdminWorkOrderPic(user);
  const isPic = isMarketingPic(user);

  const listVisibility: QuotationsListVisibility = useMemo(() => {
    if (isAdminWo) return 'admin_workorder';
    if (isMarketingSpv(user)) return 'marketing_spv';
    if (isPic) return 'marketing_pic';
    return 'all';
  }, [isAdminWo, isPic, user]);

  const menuQ = useNavigationMenuQuery(jwt);
  const showCustomPdfButton = isSuperadminUser(menuQ.data?.access_codes);
  const selfUserQ = useUsersQuery(jwt, {
    emailContains: user?.email?.trim() ?? '',
    status: 'active',
  });
  const workflowUser = useMemo(
    () => resolveQuotationWorkflowUser(user, selfUserQ.data?.rows),
    [user, selfUserQ.data?.rows],
  );
  const picAssignee = useMemo(() => {
    if (!isPic) return undefined;
    const u = workflowUser ?? user;
    if (!u) return undefined;
    return { documentId: u.documentId, id: u.id };
  }, [isPic, workflowUser, user]);
  const picAssigneeLabel =
    (workflowUser ?? user)?.username?.trim() || (workflowUser ?? user)?.email?.trim() || '';

  const [draft, setDraft] = useState<QuotationFilterDraft>(() =>
    isAdminWorkOrderPic(user) ? adminWorkorderQuotationDraft() : initialQuotationDraft,
  );
  const [appliedFilters, setAppliedFilters] = useState<QuotationsListFilters>(() =>
    isAdminWorkOrderPic(user) ? adminWorkorderQuotationsListFilters() : cloneEmptyQuotationsApplied(),
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DATA_GRID_DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_QUOTATION_SORT_MODEL);
  const appliedSort = useMemo(() => gridSortModelToListSort(sortModel), [sortModel]);
  const draftRef = useRef(draft);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [pdfPreviewRow, setPdfPreviewRow] = useState<StrapiQuotationRow | null>(null);
  const [pdfPreviewKind, setPdfPreviewKind] = useState<QuotationPdfKind>('quotation');
  const [pdfRegenerateFeedback, setPdfRegenerateFeedback] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  const [copyConfirmRow, setCopyConfirmRow] = useState<StrapiQuotationRow | null>(null);
  const [copyInProgressNumber, setCopyInProgressNumber] = useState<string>('');
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!isAdminWo) return;
    const nextDraft = adminWorkorderQuotationDraft();
    const nextApplied = adminWorkorderQuotationsListFilters();
    setDraft(nextDraft);
    draftRef.current = nextDraft;
    setAppliedFilters(nextApplied);
  }, [isAdminWo]);

  const commitFilters = useCallback(() => {
    setAppliedFilters(draftToQuotationApplied(draftRef.current));
  }, []);

  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [appliedFilters, appliedSort]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length > 0) {
      setSortModel(model);
      return;
    }
    // MUI may emit [] when cycling off `desc`; toggle instead of resetting to the same default.
    setSortModel((prev) => {
      const current = prev[0];
      if (current?.field && current.sort === 'desc') {
        return [{ field: current.field, sort: 'asc' }];
      }
      if (current?.field && current.sort === 'asc') {
        return [{ field: current.field, sort: 'desc' }];
      }
      return DEFAULT_QUOTATION_SORT_MODEL;
    });
  }, []);

  const { data, isPending, isError, error, refetch, isFetching } = useQuotationsListQuery(
    jwt,
    appliedFilters,
    appliedSort,
    paginationModel.page + 1,
    paginationModel.pageSize,
    { visibility: listVisibility, picAssignee },
  );

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  function resetFilters(): void {
    if (isAdminWo) {
      const nextDraft = adminWorkorderQuotationDraft();
      setDraft(nextDraft);
      draftRef.current = nextDraft;
      setAppliedFilters(adminWorkorderQuotationsListFilters());
      return;
    }
    setDraft(initialQuotationDraft);
    setAppliedFilters(cloneEmptyQuotationsApplied());
  }

  const onPreview = useCallback((row: StrapiQuotationRow) => {
    setPreviewDocumentId(row.documentId);
  }, []);

  const onPreviewQuotationPdf = useCallback((row: StrapiQuotationRow) => {
    setPdfPreviewKind('quotation');
    setPdfPreviewRow(row);
  }, []);

  const onViewDetail = useCallback(
    (row: StrapiQuotationRow) =>
      navigate(`/quotation/${encodeURIComponent(row.documentId)}/view`),
    [navigate],
  );

  const onEdit = useCallback(
    (row: StrapiQuotationRow) =>
      navigate(`/quotation/${encodeURIComponent(row.documentId)}/edit`),
    [navigate],
  );

  const onCopy = useCallback(async () => {
    if (!copyConfirmRow || !jwt) return;
    const sourceNumber = copyConfirmRow.quotation_number?.trim() ?? '';
    setCopyInProgressNumber(sourceNumber);
    setCopyConfirmRow(null);
    setCopyFeedback(null);
    try {
      const copied = await copyMut.mutateAsync(copyConfirmRow.documentId);
      setCopyFeedback({
        severity: 'success',
        text: `Quotation ${copied.quotation_number ?? ''} berhasil dicopy.`,
      });
      navigate(`/quotation/${encodeURIComponent(copied.documentId)}/edit`);
    } catch (err) {
      setCopyFeedback({
        severity: 'error',
        text: err instanceof Error ? err.message : 'Gagal copy quotation.',
      });
    } finally {
      setCopyInProgressNumber('');
    }
  }, [copyConfirmRow, copyMut, jwt, navigate]);

  const onRequestCopy = useCallback(
    (row: StrapiQuotationRow) => {
      if (!jwt || copyMut.isPending) return;
      setCopyConfirmRow(row);
    },
    [copyMut.isPending, jwt],
  );

  const onCancelCopy = useCallback(() => {
    if (copyMut.isPending) return;
    setCopyConfirmRow(null);
  }, [copyMut.isPending]);

  const copyInProgressMessage = useMemo(() => {
    const srcNumber = copyInProgressNumber;
    if (srcNumber) return `Sedang mengcopy quotation ${srcNumber}...`;
    return 'Sedang mengcopy quotation...';
  }, [copyInProgressNumber]);

  const copyConfirmText = useMemo(() => {
    const srcNumber = copyConfirmRow?.quotation_number?.trim();
    if (srcNumber) {
      return `Anda yakin? Ini akan mengcopy data quotation ${srcNumber}, termasuk data sample, offer, dan input user lainnya menjadi draft baru.`;
    }
    return 'Anda yakin? Ini akan mengcopy data quotation, termasuk data sample, offer, dan input user lainnya menjadi draft baru.';
  }, [copyConfirmRow?.quotation_number]);

  const clearCopyFeedback = useCallback(() => setCopyFeedback(null), []);

  const onViewSamplingSchedules = useCallback(
    async (row: StrapiQuotationRow) => {
      if (!jwt) {
        navigate('/sampling/schedules');
        return;
      }
      const path = await resolveSamplingSchedulePath(jwt, row);
      navigate(path);
    },
    [jwt, navigate],
  );

  const onCreateWorkOrder = useCallback(
    (row: StrapiQuotationRow) =>
      navigate(`/quotation/${encodeURIComponent(row.documentId)}/work-order/new`),
    [navigate],
  );

  const statusSelectOptions: QuotationFilterStatus[] = isAdminWo
    ? ['customer_approved']
    : [QUOTATION_FILTER_STATUS_ALL, ...QUOTATION_STATUS_OPTIONS.map((o) => o.value)];

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CollapsibleListFilters
        filterToggleTestId="quotations-filter-toggle"
        actions={
          <>
            {canCreateQuotation(user) ? (
              <Button
                component={RouterLink}
                to="/quotations/new"
                variant="outlined"
                color="primary"
                startIcon={<AddIcon aria-hidden />}
                data-testid="quotation-create-button"
              >
                Buat quotation baru
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              startIcon={
                isFetching ? (
                  <CircularProgress size={16} thickness={5} aria-label="Memuat ulang…" />
                ) : (
                  <RefreshIcon aria-hidden />
                )
              }
              disabled={!jwt || isFetching}
              onClick={() => void refetch()}
              data-testid="quotations-refresh-button"
            >
              Muat ulang
            </Button>
          </>
        }
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {LIST_FILTERS_APPLY_HINT}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label="No. quotation"
            value={draft.quotationNumber}
            onChange={(e) => setDraft((d) => ({ ...d, quotationNumber: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'quotations-filter-quotation-number' }}
          />
          <TextField
            label="Pelanggan"
            value={draft.customer}
            onChange={(e) => setDraft((d) => ({ ...d, customer: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="Nama atau no. pelanggan"
            inputProps={{ 'data-testid': 'quotations-filter-customer' }}
          />
          <FormControl size="small" fullWidth sx={{ minWidth: 140 }}>
            <InputLabel id="quotations-filter-status-label">Status</InputLabel>
            <Select
              labelId="quotations-filter-status-label"
              label="Status"
              value={draft.status}
              disabled={isAdminWo}
              onChange={(e) => {
                const v = e.target.value as QuotationFilterStatus;
                setDraft((d) => {
                  const next = { ...d, status: v };
                  draftRef.current = next;
                  setAppliedFilters(draftToQuotationApplied(next));
                  return next;
                });
              }}
              data-testid="quotations-filter-status"
            >
              {statusSelectOptions.map((v) => (
                <MenuItem key={v} value={v}>
                  {quotationFilterStatusLabel[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={{ minWidth: 140 }}>
            <InputLabel id="quotations-filter-contract-label">Kontrak</InputLabel>
            <Select
              labelId="quotations-filter-contract-label"
              label="Kontrak"
              value={draft.contract}
              onChange={(e) => {
                const v = e.target.value as QuotationContractUi;
                setDraft((d) => {
                  const next = { ...d, contract: v };
                  draftRef.current = next;
                  setAppliedFilters(draftToQuotationApplied(next));
                  return next;
                });
              }}
              data-testid="quotations-filter-contract"
            >
              {QUOTATION_CONTRACT_OPTIONS.map((v) => (
                <MenuItem key={v} value={v}>
                  {quotationContractLabel[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={{ minWidth: 160 }}>
            <InputLabel id="quotations-filter-consultant-label">Penawaran konsultan</InputLabel>
            <Select
              labelId="quotations-filter-consultant-label"
              label="Penawaran konsultan"
              value={draft.consultantQuote}
              onChange={(e) => {
                const v = e.target.value as QuotationConsultantUi;
                setDraft((d) => {
                  const next = { ...d, consultantQuote: v };
                  draftRef.current = next;
                  setAppliedFilters(draftToQuotationApplied(next));
                  return next;
                });
              }}
              data-testid="quotations-filter-consultant-quote"
            >
              {QUOTATION_CONSULTANT_OPTIONS.map((v) => (
                <MenuItem key={v} value={v}>
                  {quotationConsultantLabel[v]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Penanggung jawab (user)"
            value={isPic ? picAssigneeLabel : draft.assigneeContains}
            onChange={(e) => setDraft((d) => ({ ...d, assigneeContains: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            disabled={isPic}
            helperText={
              isPic
                ? 'Default PIC marketing: hanya quotation yang Anda pegang'
                : 'Username atau email assignee'
            }
            inputProps={{ 'data-testid': 'quotations-filter-assignee' }}
          />
          <TextField
            label="Disetujui oleh (user)"
            value={draft.approverContains}
            onChange={(e) => setDraft((d) => ({ ...d, approverContains: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="Username atau email approver"
            inputProps={{ 'data-testid': 'quotations-filter-approver' }}
          />
            </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {quotationAppliedIsActive(appliedFilters, isAdminWo)
              ? `Halaman ini: ${rows.length} baris · Total terfilter (server): ${total}.`
              : `Halaman ini: ${rows.length} baris · Total quotation (server): ${total}.`}
          </Typography>
          <Button
            size="small"
            color="inherit"
            disabled={
              !quotationAppliedIsActive(appliedFilters, isAdminWo) &&
              quotationDraftLooksInitial(draft, isAdminWo)
            }
            onClick={resetFilters}
          >
            Reset filter
          </Button>
        </Stack>
      </CollapsibleListFilters>
      {copyFeedback ? (
        <Alert
          severity={copyFeedback.severity}
          sx={{ mb: 2 }}
          onClose={clearCopyFeedback}
          data-testid="quotation-copy-feedback"
        >
          {copyFeedback.text}
        </Alert>
      ) : null}

      <QuotationDataGrid
        rows={rows}
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        isLoading={isPending || isFetching}
        isError={isError}
        error={error}
        user={workflowUser ?? null}
        jwt={jwt}
        onViewDetail={onViewDetail}
        onPreview={onPreview}
        onPreviewQuotationPdf={onPreviewQuotationPdf}
        pdfRegenerateFeedback={pdfRegenerateFeedback}
        onClearPdfRegenerateFeedback={() => setPdfRegenerateFeedback(null)}
        onEdit={onEdit}
        onCopy={onRequestCopy}
        onCreateWorkOrder={onCreateWorkOrder}
        onViewSamplingSchedules={onViewSamplingSchedules}
        onWorkflowUpdated={() => void refetch()}
        accessCodes={menuQ.data?.access_codes}
      />

      <QuotationPreviewDialog
        open={previewDocumentId != null}
        documentId={previewDocumentId}
        jwt={jwt}
        onClose={() => setPreviewDocumentId(null)}
      />

      <QuotationPdfPreviewDialog
        open={pdfPreviewRow != null}
        pdfKind={pdfPreviewKind}
        documentId={pdfPreviewRow?.documentId ?? null}
        quotationNumber={pdfPreviewRow?.quotation_number}
        companyName={pdfPreviewRow?.customer?.name}
        jwt={jwt}
        showCustomPdfButton={showCustomPdfButton}
        onClose={() => setPdfPreviewRow(null)}
        onRegenerateSuccess={(text) => setPdfRegenerateFeedback({ severity: 'success', text })}
        onRegenerateError={(text) => setPdfRegenerateFeedback({ severity: 'error', text })}
      />
      <Dialog open={Boolean(copyConfirmRow)} onClose={onCancelCopy} maxWidth="sm" fullWidth>
        <DialogTitle>Konfirmasi copy quotation</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{copyConfirmText}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCancelCopy} disabled={copyMut.isPending}>
            Batal
          </Button>
          <Button
            variant="contained"
            onClick={() => void onCopy()}
            disabled={copyMut.isPending}
            data-testid="quotation-copy-confirm"
          >
            Ya, copy
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={copyMut.isPending}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ mb: 2, mr: 2 }}
      >
        <Alert
          icon={<CircularProgress size={16} color="inherit" />}
          severity="info"
          variant="filled"
          sx={{ alignItems: 'center' }}
        >
          {copyInProgressMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
